import { useContext, useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AuthContext } from "../auth/auth-context";

const Navbar = () => {
  const { authState, logout, user } = useContext(AuthContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pillRef = useRef(null);
  const location = useLocation();

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const firstName = user?.name?.split(" ")[0] || "";

  const handleLogout = (e) => {
    e.preventDefault();
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    fetch(process.env.REACT_APP_BACKEND_URL + "/users/logout", {
      credentials: "include",
      method: "POST",
      headers: {
        Authorization: `Bearer ${authState.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.message === "Logout Successful") {
          logout();
          window.location.href = "/";
        }
      })
      .catch((err) => console.log(err.message));
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when viewport grows past 640px
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 640) setMobileMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close pill dropdown on click outside
  useEffect(() => {
    const onMouseDown = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  // ── Shared style helpers ─────────────────────────────────────────────────

  const desktopLinkStyle = ({ isActive }) => ({
    fontSize: "13px",
    fontWeight: 500,
    padding: "6px 12px",
    borderRadius: "6px",
    color: isActive ? "var(--brand)" : "var(--ink-soft)",
    background: isActive ? "var(--brand-light)" : "transparent",
    textDecoration: "none",
    transition: "all 0.15s",
  });

  const Divider = () => (
    <div
      style={{
        width: "1px",
        height: "20px",
        background: "var(--border)",
        margin: "0 4px",
        flexShrink: 0,
      }}
    />
  );

  // ── Mobile link row ──────────────────────────────────────────────────────

  const MobileNavLink = ({ to, children, onClick }) => (
    <NavLink
      to={to}
      onClick={onClick}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        padding: "0 20px",
        minHeight: "44px",
        fontSize: "15px",
        fontWeight: 500,
        color: isActive ? "var(--brand)" : "var(--ink-soft)",
        background: isActive ? "var(--brand-light)" : "transparent",
        textDecoration: "none",
        transition: "background 0.12s",
      })}
    >
      {children}
    </NavLink>
  );

  const MobileActionButton = ({ onClick, children, danger }) => (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        padding: "0 20px",
        minHeight: "44px",
        fontSize: "15px",
        fontWeight: 500,
        color: danger ? "var(--accent)" : "var(--ink-soft)",
        background: "none",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
      }}
    >
      {children}
    </button>
  );

  const MobileDivider = () => (
    <div
      style={{
        height: "1px",
        background: "var(--border)",
        margin: "4px 20px",
      }}
    />
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <nav
      style={{
        background: "var(--white)",
        borderBottom: mobileMenuOpen ? "none" : "1px solid var(--border)",
        position: "relative",
        zIndex: 100,
      }}
    >
      {/* ── Top bar ── */}
      <div
        style={{
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "56px",
          borderBottom: mobileMenuOpen ? "1px solid var(--border)" : "none",
        }}
      >
        {/* Logo */}
        <Link to="/" style={{ flexShrink: 0 }}>
          <h1 className="text-xl">
            <strong>taste</strong>bud.ee
          </h1>
        </Link>

        {/* Desktop nav — hidden below 640px */}
        <div className="hidden sm:flex items-center gap-1">
          <NavLink to="/about" style={desktopLinkStyle}>About</NavLink>
          <NavLink to="/pricing" style={desktopLinkStyle}>Pricing</NavLink>
          <NavLink to="/contact" style={desktopLinkStyle}>Contact</NavLink>

          {authState.isLoggedIn ? (
            <>
              <Divider />
              <NavLink to="/account/allergen-menus" style={desktopLinkStyle}>
                Allergen Menus
              </NavLink>
              <Divider />

              {/* User pill */}
              <div ref={pillRef} style={{ position: "relative" }}>
                <button
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="user-pill-btn"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 12px 4px 4px",
                    borderRadius: "40px",
                    border: "1px solid var(--border)",
                    background: "var(--white)",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    fontFamily: "inherit",
                  }}
                >
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: "var(--brand)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "white",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(user?.name)}
                  </div>
                  <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--ink)" }}>
                    {firstName}
                  </span>
                  <span style={{ fontSize: "10px", color: "var(--ink-muted)" }}>▾</span>
                </button>

                {dropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 8px)",
                      right: 0,
                      background: "var(--white)",
                      border: "1px solid var(--border)",
                      borderRadius: "10px",
                      boxShadow: "var(--shadow-md)",
                      minWidth: "210px",
                      padding: "6px",
                      zIndex: 200,
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 10px 10px",
                        borderBottom: "1px solid var(--border)",
                        marginBottom: "6px",
                      }}
                    >
                      <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--ink)" }}>
                        {user?.name}
                      </div>
                      <div style={{ fontSize: "11px", color: "var(--ink-muted)", marginTop: "2px" }}>
                        {user?.email}
                      </div>
                    </div>

                    <Link
                      to="/account/my-account"
                      onClick={() => setDropdownOpen(false)}
                      className="nav-dropdown-item"
                      style={{
                        display: "flex", alignItems: "center", gap: "8px",
                        padding: "8px 10px", borderRadius: "6px",
                        fontSize: "13px", fontWeight: 500,
                        color: "var(--ink-soft)", textDecoration: "none",
                        transition: "all 0.12s",
                      }}
                    >
                      👤 My Account
                    </Link>
                    <Link
                      to="/account/subscription"
                      onClick={() => setDropdownOpen(false)}
                      className="nav-dropdown-item"
                      style={{
                        display: "flex", alignItems: "center", gap: "8px",
                        padding: "8px 10px", borderRadius: "6px",
                        fontSize: "13px", fontWeight: 500,
                        color: "var(--ink-soft)", textDecoration: "none",
                        transition: "all 0.12s",
                      }}
                    >
                      💳 Subscription &amp; Billing
                    </Link>

                    <div style={{ height: "1px", background: "var(--border)", margin: "4px 0" }} />

                    <button
                      onClick={handleLogout}
                      className="nav-dropdown-item danger"
                      style={{
                        display: "flex", alignItems: "center", gap: "8px",
                        padding: "8px 10px", borderRadius: "6px",
                        fontSize: "13px", fontWeight: 500,
                        color: "var(--accent)", background: "none", border: "none",
                        cursor: "pointer", width: "100%", textAlign: "left",
                        fontFamily: "inherit", transition: "all 0.12s",
                      }}
                    >
                      → Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Divider />
              <NavLink to="/account/register" style={desktopLinkStyle}>Register</NavLink>
              <NavLink to="/account/login" style={desktopLinkStyle}>Login</NavLink>
            </>
          )}
        </div>

        {/* Mobile right — visible below 640px */}
        <div className="flex sm:hidden items-center gap-3">
          {/* Avatar badge when logged in */}
          {authState.isLoggedIn && (
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "var(--brand)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 600,
                color: "white",
                flexShrink: 0,
              }}
            >
              {getInitials(user?.name)}
            </div>
          )}

          {/* Hamburger / close button */}
          <button
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              borderRadius: "8px",
              border: "1px solid var(--border)",
              background: mobileMenuOpen ? "var(--brand-light)" : "var(--white)",
              color: mobileMenuOpen ? "var(--brand)" : "var(--ink-soft)",
              cursor: "pointer",
              fontSize: "18px",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {mobileMenuOpen ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {/* ── Mobile menu panel ── */}
      {mobileMenuOpen && (
        <div
          className="sm:hidden"
          style={{
            background: "var(--white)",
            borderBottom: "1px solid var(--border)",
            paddingBottom: "8px",
          }}
        >
          {/* User identity strip when logged in */}
          {authState.isLoggedIn && (
            <div
              style={{
                padding: "12px 20px 10px",
                borderBottom: "1px solid var(--border)",
                marginBottom: "4px",
              }}
            >
              <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--ink)" }}>
                {user?.name}
              </div>
              <div style={{ fontSize: "12px", color: "var(--ink-muted)", marginTop: "1px" }}>
                {user?.email}
              </div>
            </div>
          )}

          {/* Primary destination */}
          {authState.isLoggedIn && (
            <MobileNavLink to="/account/allergen-menus">
              📋 Allergen Menus
            </MobileNavLink>
          )}

          <MobileDivider />

          {/* Marketing links */}
          <MobileNavLink to="/about">About</MobileNavLink>
          <MobileNavLink to="/pricing">Pricing</MobileNavLink>
          <MobileNavLink to="/contact">Contact</MobileNavLink>

          {authState.isLoggedIn ? (
            <>
              <MobileDivider />
              <MobileNavLink to="/account/my-account">👤 My Account</MobileNavLink>
              <MobileNavLink to="/account/subscription">💳 Subscription &amp; Billing</MobileNavLink>
              <MobileDivider />
              <MobileActionButton onClick={handleLogout} danger>
                → Log out
              </MobileActionButton>
            </>
          ) : (
            <>
              <MobileDivider />
              <MobileNavLink to="/account/register">Register</MobileNavLink>
              <MobileNavLink to="/account/login">Login</MobileNavLink>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
